import React, { useMemo } from "react";
//#region types
import { DateRangeFormFieldProps, FormFieldProps } from "models/propTypes/genericFormProps";
import { UtilFormTypes } from "models/formTypes";
import { FormFieldConstants } from "utils/constants";
//#endregion
//#region components
import ReadOnlyField from "../baseFields/readOnlyField";
import DateRangePicker from "../../datePickers/dateRangePicker";
import { useFormikContext } from "formik";
//#endregion
import { DateHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function DateRangePickerFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, errors, touched, value, hideSuccess, handleOnChangeCustom, handleOnBlurCustom, readonly } =
    props;

  const hasError = !!(
    errors &&
    touched[FormFieldConstants.Util.DateRange.Start] &&
    touched[FormFieldConstants.Util.DateRange.End]
  );

  const initialValue = useMemo(() => JSON.stringify(value), []);

  const dateRangeValue = value as UtilFormTypes.DateRange;

  const success = initialValue !== JSON.stringify(value) && !errors ? true : undefined;

  const startFieldName = `${name}.${FormFieldConstants.Util.DateRange.Start}`;
  const endFieldName = `${name}.${FormFieldConstants.Util.DateRange.End}`;

  const formikProps = useFormikContext<T>();

  const onChange = (name: string, date?: Date) => {
    handleOnChangeCustom(name, date);
    extraProps?.onChange && extraProps.onChange(name, date, formikProps);
  };

  const extraProps = field.extraProps as DateRangeFormFieldProps<T>;

  return readonly ? (
    <ReadOnlyField label={field.label} href={field.href} className={cssExports.readOnlyTextField}>
      {dateRangeValue
        ? DateHelper.formatDateRange(dateRangeValue.start ?? undefined, dateRangeValue.end ?? undefined)
        : ""}
    </ReadOnlyField>
  ) : (
    <DateRangePicker
      name={name}
      disabled={props.disabled}
      inputFormat="yyyy-MM-dd"
      startDate={dateRangeValue?.start ?? undefined}
      endDate={dateRangeValue?.end ?? undefined}
      setStartDate={onChange.bind(null, startFieldName)}
      setEndDate={onChange.bind(null, endFieldName)}
      error={hasError}
      success={!hideSuccess && success}
      onStartBlur={handleOnBlurCustom.bind(null, startFieldName)}
      onEndBlur={handleOnBlurCustom.bind(null, endFieldName)}
      onFocus={field.onFocusHandler}
      minStartDate={extraProps?.minStartDate}
      maxStartDate={extraProps?.restrictCrossDateSelection ? dateRangeValue?.end : extraProps?.maxStartDate}
      minEndDate={extraProps?.restrictCrossDateSelection ? dateRangeValue?.start : extraProps?.minEndDate}
      maxEndDate={extraProps?.maxEndDate}
    />
  );
}

export default DateRangePickerFormField;
